import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {Col, Row} from "reactstrap";
import {DropdownForm} from "../../base/dropdown/dropdown";
import {DatePickerForm} from "../../base/date-picker";
import Button from "../../base/button/button";
import Form from "../../base/form";
import useIsMounted from "../../../hooks/use-is-mounted";
import {InputForm} from "../../base/input/input";
import Api from "../../../../core/services/api/api";
import {Close} from "@mui/icons-material";
import useIndustry from "../../../hooks/use-industry";
import {MuiRFF, Yup} from '../../form';
import DateUtils from "../../../../core/services/date-utils";
import MultiSelectPartSearchSection from "../../text-fields/autocomplete/part-autocomplete/multi-select-part-search-section";

const formValues = {
    parts: 'parts',
    reportName: 'reportName',
    category: 'category',
    reason: 'reason',
    startDate: 'startDate',
    endDate: 'endDate',
    industry: 'industry',
}

const schema = Yup.object().shape({
    [formValues.reportName]: Yup.string().trim().nullable().required(ValidateMessages.required),
    [formValues.category]: Yup.object().nullable(),
    [formValues.industry]: Yup.object().nullable(),
    [formValues.reason]: Yup.object().nullable(),
    [formValues.startDate]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .maxDate(Yup.ref(formValues.endDate), ValidateMessages.lessThan('End Date'))
        .disableFutureDate(ValidateMessages.maxDate("today"), 'day')
        .nullable(),
    [formValues.endDate]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .minDate(Yup.ref(formValues.startDate), ValidateMessages.greaterThan('Start Date'))
        .disableFutureDate(ValidateMessages.maxDate("today"), 'day')
        .nullable(),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const InventoryAdjustmentReportSearchSection = ({singleProduct, loading, handleAddingReport, defaultProduct}) => {
    const [reasons, setReasons] = useState([]);
    const [parts, setParts] = useState([]);
    const isMounted = useIsMounted();

    const {cachedIndustry, fetchIndustry, fetchedIndustry, industries, categoriesOfFetchedIndustry, hasNoIndustries} = useIndustry();
    const categories = useMemo(() => categoriesOfFetchedIndustry
            ?.filter(category => !category.parentId)
            ?.map(e => ({id: e.id, title: e.title})) ?? []
        , [categoriesOfFetchedIndustry]);

    /**@type {React.MutableRefObject<formApi>}*/
    const formRef = useRef();
    const valueRef = useRef();

    /**
     * Automatically fetches the cached industry when the component is mounted
     */
    useEffect(() => {
        if (!cachedIndustry?.id)
            return;
        void fetchIndustry({industryId: cachedIndustry.id});
    }, [cachedIndustry?.id, fetchIndustry]);

    /**
     * Automatically fetches the industry if there is only one industry available and it's not already fetched or selected
     */
    useEffect(() => {
        if (industries?.length === 1) {
            const industry = industries[0];
            void fetchIndustry({industryId: industry.id});
        }
    }, [fetchIndustry, industries]);

    /**
     * Calls the api for getting the list of reasons from server
     * @return {Promise<void>}
     */
    const getReasons = useCallback(async () => {
        const response = await Api.getInventoryAdjustmentsReasons();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setReasons((response?.data ?? []).map(e => ({
                id: e.id,
                code: e.code,
                reason: e.title,
                operator: e.modifiedDateTime,
                submittedDate: e.modifiedBy,
                isDefault: e.isDefault,
            })))
        }
    }, [isMounted])

    /**
     * With initial render of the component, Calls the function for getting the list of all reasons
     */
    useEffect(() => {
        getReasons().then();
    }, [getReasons])


    /**
     * Submits the changes in the search section by changing the query parameters in the url
     * @param values
     * @param {formApi} form
     */
    const onSubmit = async (values, form) => {
        const res = await handleAddingReport({
            parts: parts ?? [],
            category: values[formValues.category],
            reason: values[formValues.reason],
            startDate: values[formValues.startDate] ? DateUtils.UTCToServer(values[formValues.startDate]).format(DateUtils.withoutOffsetFormat) : undefined,
            endDate: values[formValues.endDate] ? DateUtils.UTCToServer(values[formValues.endDate]).format(DateUtils.withoutOffsetFormat) : undefined,
            reportName: values[formValues.reportName],
        });
        if (!isMounted())
            return;
        if (!!res)
            return res;
        setTimeout(() => {
            if (!isMounted())
                return;
            form.change(formValues.reportName, null);
            form.change(formValues.reason, null);
            form.change(formValues.startDate, DateUtils.localNowToUTC().subtract(30, 'day'));
            form.change(formValues.endDate, null);
            form.resetFieldState(formValues.reportName);
            form.resetFieldState(formValues.reason);
            form.resetFieldState(formValues.startDate);
            form.resetFieldState(formValues.endDate);
            if (!singleProduct) {
                if (!hasNoIndustries) {
                    form.change(formValues.category, null);
                    form.resetFieldState(formValues.category);
                    if (industries.length > 1) {
                        form.change(formValues.industry, fetchedIndustry);
                        form.resetFieldState(formValues.industry);
                    }
                }
            }
        }, 300);
        return res;
    }

    /**
     * Handles removing the selected category from the list when remove icon button clicked on input chips
     * @param event
     * @param category
     * @private
     */
    const _removeSelectedCategory = (event, category) => {
        const isLastItem = (valueRef.current?.category?.length ?? 0) < 2;
        formRef.current.change(formValues.category, isLastItem
            ? undefined
            : valueRef.current?.category?.filter(e => e.id !== category.id)
        );
        event.stopPropagation();
        event.preventDefault();
    }

    /**
     * Renders the category select options
     * @param item
     * @param index
     * @param length
     * @return {JSX.Element}
     */
    const renderSelectedCategoriesValue = (item, index, length) => {
        return index < 2
            ? <Button key={item.id ?? item.label ?? item.name ?? item.title} onClick={(e) => _removeSelectedCategory(e, item)}
                      className={'multi-select-input-chips'}>
                <div className={'remove-icon-container'}>
                    <Close className='icon extraSmall'/>
                </div>
                <p>
                    {item.label ?? item.name ?? item.title ?? ''}
                </p>
            </Button>
            : index === (length - 1)
                ? <div key={item.id ?? item.label ?? item.name ?? item.title} style={{height: '26px'}}
                       className={'d-flex align-items-center justify-content-center'}>
                    <p className={'text-sm font-weight-regular'}>
                        {`+${length - 2} more`}
                    </p>
                </div>
                : <></>
    }

    /**
     * Handles removing the selected reason from the list when remove icon button clicked on input chips
     * @param event
     * @param reason
     * @private
     */
    const _removeSelectedReason = (event, reason) => {
        const isLastItem = (valueRef.current?.reason?.length ?? 0) < 2;
        formRef.current.change(formValues.reason, isLastItem
            ? undefined
            : valueRef.current?.reason?.filter(e => e.id !== reason.id)
        );
        event.stopPropagation();
        event.preventDefault();
    }

    /**
     * Renders the category select options
     * @param item
     * @param index
     * @param length
     * @return {JSX.Element}
     */
    const renderSelectedReasonsValue = (item, index, length) => {
        return index < 2
            ? <Button
                key={item.id}
                onClick={(e) => _removeSelectedReason(e, item)}
                className={'multi-select-input-chips'}
            >
                <div className={'remove-icon-container'}>
                    <Close className='icon extraSmall'/>
                </div>
                <p>
                    {`${item.code} - ${item.reason}`}
                </p>
            </Button>
            : index === (length - 1)
                ? <div key={item.id ?? item.label ?? item.name ?? item.title} style={{height: '26px'}}
                       className={'d-flex align-items-center justify-content-center'}>
                    <p className={'text-sm font-weight-regular'}>
                        {`+${length - 2} more`}
                    </p>
                </div>
                : <></>
    }

    /**
     * Empties the sub-category values of a particular category that has been changed.
     * @param e
     * @param form
     */
    const onIndustryChange = (e, form) => {
        form.change(formValues.category, []);
        if (!e.target.value) {
            fetchIndustry({industryId: cachedIndustry?.id}).then();
        } else {
            fetchIndustry({industryId: e.target.value?.id}).then();
        }
    }

    const initialValues = useMemo(() => ({
        [formValues.industry]: fetchedIndustry,
        [formValues.startDate]: DateUtils.localNowToUTC().subtract(30, 'day'),
    }), [fetchedIndustry])

    return (
        <Form
            onSubmit={onSubmit}
            validate={validate}
            initialValues={initialValues}
            className={'d-flex w-100 align-items-center justify-content-center'}
            render={({submitting, values, form}) => {
                formRef.current = form;
                valueRef.current = values;
                return (
                    <Row className='d-flex w-100 align-items-center justify-content-center'>
                        {
                            !singleProduct &&
                            <Col xs={12} className='mx-2 mb-3 align-items-center justify-content-center flex-wrap'>
                                <MultiSelectPartSearchSection
                                    includeDefaultPart={singleProduct}
                                    defaultSelectedProduct={defaultProduct}
                                    parts={parts}
                                    setParts={setParts}
                                />
                            </Col>
                        }
                        <Col xs={12} md={6} className={'mb-3'}>
                            <InputForm
                                label={'Report Name'}
                                type={'text'}
                                name={formValues.reportName}
                                required={required[formValues.reportName]}
                            />
                        </Col>
                        <Col xs={12} md={6} className={'mb-3'}>
                            <DropdownForm
                                padding={'3px 15px'}
                                label={'Reasons'}
                                className={'py-0'}
                                name={formValues.reason}
                                required={false}
                                renderMultipleSelectValue={renderSelectedReasonsValue}
                                multiple
                                data={reasons.map(e => ({
                                    value: e,
                                    label: `${e.code} - ${e.reason}`,
                                    id: e.id,
                                })) ?? []}
                            />
                        </Col>
                        {
                            !singleProduct &&
                            <>
                                {
                                    industries.length > 1 &&
                                    <Col xs={12} md={6} className={'mb-3'}>
                                        <DropdownForm
                                            label={'Industry'}
                                            name={formValues.industry}
                                            required={false}
                                            data={industries?.map(e => ({value: e, label: e.title, id: e.id})) ?? []}
                                            onChange={(e) => onIndustryChange(e, form)}
                                        />
                                    </Col>
                                }
                                {
                                    !hasNoIndustries &&
                                    <Col xs={12} md={6} className={'mb-3'}>
                                        <DropdownForm
                                            renderMultipleSelectValue={renderSelectedCategoriesValue}
                                            padding={'3px 15px'}
                                            label={'Categories'}
                                            className={'py-0'}
                                            name={formValues.category}
                                            required={false}
                                            multiple
                                            data={categories.map(e => ({
                                                value: e,
                                                label: e.title,
                                                id: e.id,
                                            })) ?? []}
                                        />
                                    </Col>
                                }
                            </>
                        }
                        <Col xs={12} md={6} className={'mb-3'}>
                            <DatePickerForm
                                name={formValues.startDate}
                                label={'Start Date'}
                                required={required[formValues.startDate]}
                                maxDate={values[formValues.endDate]}
                                disableFuture
                            />
                        </Col>
                        <Col xs={12} md={6} className={'mb-3'}>
                            <DatePickerForm
                                name={formValues.endDate}
                                label={'End Date'}
                                required={required[formValues.endDate]}
                                minDate={values[formValues.startDate]}
                                disableFuture
                            />
                        </Col>
                        <Col xs={12} className='px-3 mt-2 align-items-center row no-gutters justify-content-center align-items-center'>
                            <Button
                                disabled={!!submitting || loading}
                                type={'submit'}
                                variant={'contained'}
                                color={'primary'}
                                className='py-3 px-8'
                            >
                                {
                                    submitting
                                        ? "Adding Report..."
                                        : "Add Report"
                                }
                            </Button>
                        </Col>
                    </Row>
                );
            }}
        />
    );
}

export default InventoryAdjustmentReportSearchSection;
